/* eslint-disable react-hooks/exhaustive-deps */
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import * as Sentry from '@sentry/nextjs'
import styles from './Layout.module.scss'
import { useTranslation } from 'next-i18next'
import DynamicStyles from './DynamicStyles/DynamicStyles'
import { ACCOUNT_PAGES, gtm_view } from 'src/core/helpers'
import { Country, HeaderState, User, PosibleLocale } from 'src/core/models'
import { getCtCookie } from 'src/core/helpers'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { fetchAuthenticatedUser } from 'src/features/users/api'
import { deleteAllCookies, removeToken } from 'src/core/helpers/auth'
import { TheFooter, ScrollTop, Sidebar } from 'src/components'
import {
  setVerifyTokenRedirectLogin,
  verifyTokenSelector,
  setCountryListData,
  getCurrencyList,
  checkCookies,
  verifyToken,
  setCurrency,
  selectUser,
  trackEvent,
  setUser,
  changeUserLanguage,
  getHeaderData
} from 'src/features'

import { GlobalHeader } from '@climatetrade/ui'

interface LayoutProps {
  children: React.ReactNode
  protected: boolean
  required: string[]
  isAdminOrDeveloper: boolean
  headerData: HeaderState
  countries: Country[]
}

const Layout: React.FC<LayoutProps> = ({ children, ...props }) => {
  const {
    'protected': isRouteProtected,
    required: isAuthRequired,
    isAdminOrDeveloper,
    headerData,
    countries
  } = props
  const { i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const user = useAppSelector(selectUser)
  const token = Cookies.get(process.env.NEXT_PUBLIC_TOKEN_NAME)
  const { redirectLogin } = useAppSelector(verifyTokenSelector)

  const [currentHeaderData, setCurrenctHeaderData] = useState<HeaderState>(headerData)

  const languageCookie = getCtCookie('ct-language')

  useEffect(() => {
    const updateHeaderData = async () => {
      const newData = await getHeaderData(languageCookie as PosibleLocale)
      setCurrenctHeaderData(newData)
    }
    updateHeaderData()
    validateLanguage(languageCookie as PosibleLocale)
  }, [languageCookie])

  useEffect(() => localStorage.setItem('prevRoute', `/accounts${router.asPath}`), [router])

  useEffect(() => {
    if (user) Sentry.setUser({ email: user?.email })
  }, [user])

  useEffect(() => {
    dispatch(setCountryListData(countries))
    dispatch(getCurrencyList())
  }, [dispatch])

  useEffect(() => {
    if (isRouteProtected) {
      if (user) return // Its called on login-form submit
      fetchAuthenticatedUser().then((user: User) => {
        validateLanguage(user?.language)
        dispatch(setUser(user))
        dispatch(setCurrency(user?.currency_token_code))
        if (isAuthRequired?.some((k: string) => !user[k])) {
          router.push('/auth/login')
        }
        if (isAdminOrDeveloper && !(user?.is_superuser || user?.is_project_developer)) {
          router.push('/auth/login')
        }
      })
    }
  }, [])


  const validateLanguage = (language: string) => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language)
      router.push(router.asPath, undefined, { locale: language })
    }
  }

  const route = router.route
  useEffect(() => {
    // Track Event To CT Micro-Server
    dispatch(checkCookies())
    dispatch(trackEvent(route, 'PAGE_VIEW'))

    // Track Page View With GTM
    gtm_view({ 'page_name': ACCOUNT_PAGES[route] })

    /* TOKEN VERIFY */
    if (token) { dispatch(verifyToken(token)) }
  }, [route])


  useEffect(() => {
    if (redirectLogin) {
      removeToken()
      deleteAllCookies()
      dispatch(setVerifyTokenRedirectLogin(false))
      router.push('/auth/login')
    }
  }, [redirectLogin])

  const handleLanguageChange = (languageValue: string) => {
    const currentLanguage = i18n.language
    const language = languageValue?.toLowerCase()
    if (!languageValue || currentLanguage === language || typeof languageValue === 'undefined') return
    i18n.changeLanguage(language)
    router.push(router.asPath, undefined, { locale: language })
    if (user) { dispatch(changeUserLanguage({ language: language })) }
  }

  const handleCurrencyChange = (currencyCode: string) => {
    dispatch(setCurrency(currencyCode))
  }

  return (
    <React.Fragment>
      <DynamicStyles />
      <GlobalHeader
        data={currentHeaderData}
        onLanguageChange={handleLanguageChange}
        onCurrencyChange={handleCurrencyChange} />
      <ScrollTop />
      <main className={styles.content}>
        <Sidebar>
          {children}
        </Sidebar>
      </main>
      <TheFooter />
    </ React.Fragment>
  )
}

export default Layout
